import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LitleDisclosure from "@components/disclosure/LitleDisclosure";
import {
  SvgPrimes,
  SvgPrimesList,
  DeleteElementPrime,
  SvgH,
  SvgMin
} from "@routes/IconeSvg";
import ArrowLitle from "@assets/image/icones/calendarParametre/Vector.svg";
import { useForm, Controller } from "react-hook-form";
import { ListboxSimple } from "@components/tailwindComponents";
import imgClock from "@image/icones/icon_clock.svg";
import moment from "moment-timezone";

const NAME_HOURS = "hours";
const NAME_MINUTES = "minutes";

const TimeInput = ({
  label,
  value,
  onChange,
  min,
  max,
  icon,
  invalid = false,
  isSaving,
  disabled = false
}) => (
  <div
    className={`arh-w-[35%] arh-p-[0.3rem] arh-rounded-[4px] arh-border arh-border-solid arh-flex arh-items-center arh-h-[2.25rem] 
   ${ disabled ? "arh-bg-[#F5F5F5] arh-border-greyDisabledBorder  " : isSaving && !invalid ? "arh-border-invalidColorTextInput" : "arh-border-[#ECECEC]"}`}
  >
    <input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Math.max(min, Math.min(max, +e.target.value)))}
      className={`arh-w-full arh-border-none arh-p-0  arh-text-left arh-font-poppins arh-font-normal arh-text-xs disabled:arh-pointer-events-none disabled:arh-cursor-none read-only:arh-cursor-default read-only:arh-border-greyDisabledBorder read-only:arh-bg-greyDisabled read-only:arh-text-greyDisabledText read-only:placeholder:arh-text-greyDisabledText ${isSaving && !invalid ? "arh-text-invalidColorTextInput placeholder:arh-text-invalidColorTextInput" : "arh-text-darkColor"}`}
      placeholder={label}
      readOnly={disabled}
      disabled={disabled}
    />
    <div>{icon}</div>
  </div>
);

function ContainerPrime({
  lists = [],
  setSelectedPrimes,
  isSaving,
  selectedDate,
  initPrimes = [],
  isEdit = false,
  setIdPrimesToDelete
}) {
  const { t } = useTranslation("prime.prime");
  const { control, watch, setValue } = useForm();
  const [primeTimes, setPrimeTimes] = useState({});
  const selectedPrimes = watch("prime", []);
  const [IDsToBeDeleted, setIdsToBeDeleted] = useState([]);
  const definitiveLists = useMemo(()=>isEdit ? lists : lists?.filter((_prime)=> !Boolean(_prime?.isAutomatic)),[lists,isEdit])
  
  useEffect(() => {
    setValue(
      "prime",
      lists?.filter((_list) =>
        initPrimes.some((_prime) => _prime.Primes === _list._id)
      )
    );
  const selectedDateDuration = Math.round(moment.duration(moment(selectedDate.endDate).diff(moment(selectedDate.startDate))).asMinutes())
    const initialPrimeTimes = initPrimes.reduce((acc, primeBloHour) => {
      acc[primeBloHour.Primes] = {
        ...primeBloHour,
        hours: Math.floor(primeBloHour.duration / 60),
        minutes: primeBloHour.duration % 60,
        isValid: true,
        _id: primeBloHour._id
      };
      return acc;
    }, {});
    const initialBloc = initPrimes.reduce((acc, primeBloHour) => {
      acc[primeBloHour._id] = {
        ...primeBloHour,
        hours: Math.floor(primeBloHour.duration / 60),
        minutes: primeBloHour.duration % 60,
        isValid: Boolean((selectedDateDuration - ((parseInt(primeBloHour?.hours) || 0) * 60 + parseInt(primeBloHour.minutes || 0))) < 0 && isSaving),
        _id: primeBloHour._id
      } ; return acc;
    }, {});
    setPrimeTimes(initialPrimeTimes);
    setSelectedPrimes(initialBloc)
  }, [initPrimes, lists, isSaving]);

  const primesLists = useMemo(() => {
    return selectedPrimes
      ?.map((prime) => {
        const primeTime = primeTimes[prime._id];
        if (primeTime) {
          return {
            ...prime,
            isValid: Boolean(
              isSaving &&
                (primeTime?.hours || primeTime?.minutes) &&
                moment(selectedDate.endDate).isSameOrAfter(
                  moment(selectedDate.startDate, "YYYY-MM-DD HH:mm")
                    .add(primeTime?.hours || 0, "hours")
                    .add(primeTime?.minutes || 0, "minutes"),
                  "minutes"
                )
            ),
            hours: primeTime?.hours || 0,
            minutes: primeTime?.minutes || 0
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [selectedPrimes, primeTimes, isSaving]); 
  // Suppression d'une prime
  const removePrime = (primeId, primeBloHourId = null) => {
    const updatedPrimes = selectedPrimes.filter(
      (prime) => prime._id !== primeId
    );
    setValue("prime", updatedPrimes);
    setPrimeTimes((prev) => {
      const { [primeId]: _, ...rest } = prev;
      setSelectedPrimes(rest);
      return rest;
    });
    if (primeBloHourId) {
      setIdsToBeDeleted((prev) => {
        const updatedIds = [...prev, primeBloHourId];
        setIdPrimesToDelete(updatedIds);
        return updatedIds;
      });
    }
  };

  const updatePrimeTime = (primeId, field, value, _id = null) => {
    setPrimeTimes((prev) => {
      const updatedPrimeTimes = {
        ...prev,
        [primeId]: {
          ...prev[primeId],
          [field]: value || ""
        }
      };

      // Recalculer la validité de la prime
      const primeTime = updatedPrimeTimes[primeId];

      const isValid =
        (primeTime.hours || primeTime.minutes) &&
        moment(selectedDate.endDate).isSameOrAfter(
          moment(selectedDate.startDate)
            .add(primeTime.hours || 0, "hours")
            .add(primeTime.minutes || 0, "minutes"),
          "minutes"
        );

      updatedPrimeTimes[primeId].isValid = isValid;
      updatedPrimeTimes[primeId]._id = _id;
      setSelectedPrimes(updatedPrimeTimes);
      return updatedPrimeTimes;
    });
  };
  if(!definitiveLists?.length) return ""
  return (
    <div className="arh-flex arh-w-full arh-flex-col">
      <label className="arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium arh-text-black">
        {t("Prime(s)")}
      </label>

      <div className="arh-w-full">
        <LitleDisclosure
          classParent="arh-rounded arh-border arh-border-solid arh-border-[#ECECEC]"
          LitleTitle={true}
          titleIcon={SvgPrimes()}
          title={t("Ajouter une prime")}
          defaultOpen={isEdit}
          //defaultOpen
          spaceTitle={true}
          classSpaceTitle="arh-ml-[0.848rem]"
          customArrow={true}
          customArrowShape={ArrowLitle}
        >
          <div className="arh-p-[1rem]">
            <Controller
              control={control}
              name="prime"
              defaultValue={[]}
              render={({ field: { onChange, value } }) => (
                <ListboxSimple
                  placeholder={t("Sélectionner des primes")}
                  optionList={definitiveLists}
                  selectedValue={value}
                  setSelectedValue={(newPrimes) => {
                    setPrimeTimes((prev) => {
                      const updatedPrimeTimes = { ...prev };

                      newPrimes.forEach((prime) => {
                        if (!updatedPrimeTimes[prime._id]) {
                          const existingPrime = initPrimes.find(
                            (initPrime) => initPrime.Primes === prime._id
                          );

                          // Mise à jour ou ajout de la prime spécifique
                          updatedPrimeTimes[prime._id] = {
                            hours: existingPrime
                              ? Math.floor(existingPrime.duration / 60)
                              : "",
                            minutes: existingPrime
                              ? existingPrime.duration % 60
                              : "",
                            _id: existingPrime ? existingPrime._id : null
                          };
                        }
                      });
                      if (initPrimes?.length) {
                        // Créer un ensemble des IDs présents dans les nouvelles primes pour une recherche rapide
                        const newPrimeIds = new Set(
                          newPrimes.map((prime) => prime._id)
                        );

                        // Filtrer les IDs des primes initiales qui ne sont pas dans les nouvelles primes
                        const idsToRemove = initPrimes
                          .filter(
                            (primeBloc) => !newPrimeIds.has(primeBloc.Primes)
                          )
                          .map((primeBloc) => primeBloc._id);

                        if (idsToRemove.length) {
                          // Créer un ensemble des IDs à supprimer actuels
                          const idsToBeDeletedSet = new Set(IDsToBeDeleted);

                          // Ajouter les nouveaux IDs à supprimer
                          idsToRemove.forEach((id) =>
                            idsToBeDeletedSet.add(id)
                          );
                          const updatedIds = [...idsToBeDeletedSet].filter(
                            (id) => !newPrimeIds.has(id)
                          );
                          setIdsToBeDeleted(updatedIds);
                          setIdPrimesToDelete(updatedIds);
                        }
                      }

                      return updatedPrimeTimes;
                    });

                    onChange(newPrimes);
                  }}
                  optionTextAttribut="designation"
                  multiple
                  customTranslator={t}
                  color="orange"
                  showReset
                />
              )}
            />

            {/* Liste des primes sélectionnées */}
            <div className="arh-w-full arh-flex arh-flex-col arh-my-[1rem]">
              {primesLists.map((prime) => {
                const primeTime = primeTimes[prime._id] || {
                  hours: 0,
                  minutes: 0
                };

                return (
                  <div
                    key={prime._id}
                    className="arh-w-full arh-flex arh-items-start arh-my-[0.3rem] arh-relative"
                  >
                    <div className="arh-flex-1">
                      <LitleDisclosure
                        classParent="arh-rounded arh-border arh-border-solid arh-border-[#ECECEC]"
                        LitleTitle={true}
                        titleIcon={SvgPrimesList()}
                        title={prime.designation}
                        // defaultOpen={isEdit}
                        defaultOpen
                        spaceTitle={true}
                        classSpaceTitle="arh-ml-[0.848rem]"
                        customArrow={true}
                        customArrowShape={ArrowLitle}
                      >
                        <div className="arh-p-[1rem]">
                          {/* Inputs pour heures et minutes */}
                          <div className="arh-w-full arh-flex arh-items-center arh-justify-between">
                            <TimeInput
                              label="0"
                              value={primeTime.hours}
                              onChange={(value) =>
                                updatePrimeTime(
                                  prime._id,
                                  NAME_HOURS,
                                  value,
                                  primeTime._id
                                )
                              }
                              min={0}
                              max={23}
                              icon={SvgH()}
                              invalid={primeTime.isValid}
                              isSaving={isSaving}
                              disabled={Boolean(prime.isAutomatic && !isEdit)}
                            />
                            <TimeInput
                              label="0"
                              value={primeTime.minutes}
                              onChange={(value) =>
                                updatePrimeTime(
                                  prime._id,
                                  NAME_MINUTES,
                                  value,
                                  primeTime._id
                                )
                              }
                              min={0}
                              max={59}
                              icon={SvgMin()}
                              invalid={primeTime.isValid}
                              disabled={Boolean(prime.isAutomatic && !isEdit)}
                              isSaving={isSaving}
                            />

                            {/* Temps total */}
                            <div className="arh-flex arh-w-fit arh-items-center arh-space-x-2 arh-rounded-[40px] arh-bg-[#fdf6f1] arh-px-2 arh-py-1.5">
                              <img
                                src={imgClock}
                                alt="Clock Icon"
                              />
                              <span className="arh-font-poppins arh-text-xs arh-font-semibold arh-text-[#EA8A60]">
                                {`${primeTime.hours || "00"}h${primeTime.minutes || "00"}`}
                              </span>
                            </div>
                          </div>
                        </div>
                      </LitleDisclosure>
                    </div>
                    <div
                      className="arh-absolute -arh-right-[0.8rem] arh-top-[0.8rem] arh-cursor-pointer"
                      onClick={() => removePrime(prime._id, primeTime._id)}
                    >
                      {DeleteElementPrime()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </LitleDisclosure>
      </div>
    </div>
  );
}

export default ContainerPrime;
